"use client"

import React, { useState, useEffect } from "react"
import CountUp from "react-countup"
import { useInView } from "react-intersection-observer"
import { CheckCircle } from "lucide-react"
import PaintSplatter from "./paint-splatter"

export default function About({ profile }) {
  const colors = {
    primary: "#7749F8",
    primaryDark: "#6438E4",
    secondary: "#FFBA07",
    white: "#FFFFFF",
    black: "#000000",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
  }

  const { ref: counterRef, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  })

  const containerStyle = {    
    padding: "4rem 1rem",
    position: "relative",
  }

  const flexContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    margin: "0 -1rem",
    position: "relative",
    maxWidth: "1200px",
    margin: "0 auto",
    zIndex: 10,
  }

  const columnStyle = {
    width: "100%",
    padding: "0 1rem",
    marginBottom: "2.5rem",
    animation: "fadeIn 1s ease-out forwards",
  }

  const bioStyle = {
    textAlign: "center",
    fontSize: "2rem",
    fontWeight: "bold",
    padding: "2rem",
    background: `linear-gradient(90deg, ${colors.white}, ${colors.secondary} 50%, ${colors.white})`, // White to yellow/gold to white
    color: colors.primary,
    borderRadius: "0.5rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  }

  const yearsContainerStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "1.25rem",
  }

  const yearsCounterStyle = {
    flexShrink: 0,
    textAlign: "center",
    marginRight: "1rem",
    position: "relative",
  }

  const yearsCounterBgStyle = {
    position: "absolute",
    inset: 0,
    backgroundColor: colors.secondary,
    opacity: 0.1,
    borderRadius: "9999px",
    filter: "blur(0.5rem)",
  }

  const yearsNumberStyle = {
    fontSize: "3.75rem",
    marginBottom: 0,
    fontWeight: "bold",
    position: "relative",
    zIndex: 10,
  }

  const yearsTextStyle = {
    marginBottom: 0,
    position: "relative",
    zIndex: 10,
  }

  const paragraphStyle = {
    marginBottom: "1rem",
  }

  const checkItemStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "0.75rem",
  }

  const checkIconStyle = {
    color: colors.primary,
    marginRight: "0.75rem",
  }

  const primaryButtonStyle = {
    display: "inline-block",
    backgroundColor: colors.primary,
    color: colors.white,
    padding: "0.75rem 1.25rem",
    marginTop: "0.75rem",
    borderRadius: "0.25rem",
    transition: "background-color 0.3s",
    textDecoration: "none",
    cursor: "pointer",
  }

  const imageContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    margin: "0 -0.5rem",
    marginBottom: "1rem",
  }

  const imageWrapperStyle = {
    width: "100%",
    padding: "0 0.5rem",
    marginBottom: "1rem",
    position: "relative",
  }

  const imageSplatterStyle = {
    position: "absolute",
    top: "-0.75rem",
    left: "-0.75rem",
    width: "4rem",
    height: "4rem",
    backgroundColor: colors.secondary,
    opacity: 0.2,
    borderRadius: "9999px",
    filter: "blur(0.5rem)",
  }

  const imageStyle = {
    width: "100%",
    borderRadius: "0.25rem",
    position: "relative",
    zIndex: 10,
  }

  const statsContainerStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "0.75rem",
    position: "relative",
    zIndex: 10,
  }

  const statsTitleStyle = {
    borderRight: `1px solid ${colors.gray[300]}`,
    paddingRight: "0.75rem",
    marginRight: "0.75rem",
    marginBottom: 0,
  }

  const statsNumberStyle = {
    color: colors.primary,
    fontWeight: "bold",
    marginBottom: 0,
  }

  React.useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= 1024

      const leftColumn = document.getElementById("about-left-column")
      const rightColumn = document.getElementById("about-right-column")
      const imageWrappers = document.querySelectorAll(".about-image-wrapper")

      if (leftColumn && rightColumn) {
        if (isLargeScreen) {
          leftColumn.style.width = "50%"
          rightColumn.style.width = "50%"
          leftColumn.style.marginBottom = "0"

          imageWrappers.forEach((wrapper) => {
            wrapper.style.width = "50%"
            if (wrapper.classList.contains("first-image")) {
              wrapper.style.marginBottom = "0"
            }
          })
        } else {
          leftColumn.style.width = "100%"
          rightColumn.style.width = "100%"

          imageWrappers.forEach((wrapper) => {
            wrapper.style.width = "100%"
          })
        }
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <div style={containerStyle} id="about">
      <PaintSplatter position="bottom-right" size="lg" opacity={0.15} rotation={45} />
      <PaintSplatter position="center-left" size="md" opacity={0.1} rotation={-30} />

      <div style={flexContainerStyle}>
        <div id="about-left-column" style={columnStyle}>
          <h1 style={bioStyle}>{profile?.bio || "Loading biography..."}</h1>
        </div>
      </div>
    </div>
  )
}

